import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { Box } from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { useState, useCallback } from "react";
import { audioToBase64 } from "../../../utils";
import { useDropzone } from "react-dropzone";
import { useTheme } from "@mui/material/styles";

interface MediaRow {
  fileName?: string;
  [key: string]: unknown;
}

interface MediaInfo {
  fileName?: string;
  fileContent?: string;
  isRecorded?: boolean;
  isUploaded?: boolean;
  errorType?: string;
  [key: string]: unknown;
}

interface UploadedFile {
  id: number;
  lastModified: number;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}

interface Props {
  row: MediaRow;
  handleMediaInfo: (info: MediaInfo | null) => void;
}

const MediaMgtFileUpload = ({ row, handleMediaInfo }: Props) => {
  const theme = useTheme();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [base64String, setBase64String] = useState<string>("");

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const mediaFiles = [
        ...files.map((f) => f as unknown as File),
        ...acceptedFiles,
      ];
      if (mediaFiles.length === 0) {
        setBase64String("");
        setFiles([]);
        handleMediaInfo({
          errorType: "unsupported",
        });
      } else {
        const base64Result = (await audioToBase64(mediaFiles[0])) as string;
        const filesWithId: UploadedFile[] = mediaFiles.map((item, index) => {
          const fileItem = item as File;
          return {
            id: index,
            lastModified: fileItem.lastModified,
            name: fileItem.name,
            size: fileItem.size,
            type: fileItem.type,
            webkitRelativePath: fileItem.webkitRelativePath,
          };
        });
        setBase64String(base64Result);
        setFiles(filesWithId);
        if (mediaFiles.length > 0) {
          handleMediaInfo({
            fileName: filesWithId[0].name,
            fileContent: base64Result,
            isRecorded: false,
            isUploaded: true,
          });
        }
      }
    },
    [files],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "audio/.wav": [".wav"] },
    onDrop,
  });

  const removeFile = () => {
    setFiles([]);
    handleMediaInfo(null);
  };

  const handleOnError = () => {
    handleMediaInfo({
      errorType: "invalid",
    });
  };

  return (
    <section className="container">
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
      </div>
      <Box className="border p-3" sx={{ padding: 2 }}>
        {files.length === 0 && (
          <>
            <Box
              sx={{
                fontSize: 11,
                color: theme.palette.redAlert.main,
                marginTop: 1,
                marginBottom: 0.5,
                width: "100%",
              }}
            >
              <SbuxTranslate>{`Warning: Only upload files you have legal permission to use.`}</SbuxTranslate>
            </Box>

            <Box
              sx={{
                fontSize: 11,
                marginTop: 0.2,
                marginBottom: 1,
                width: "100%",
              }}
            >
              <SbuxTranslate>{`Accepted file type: .wav with 32bit or ULAW encoding`}</SbuxTranslate>
            </Box>

            <Box {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} accept="audio/wav" />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  border: "2px dotted green",
                  marginTop: 1,
                  marginBottom: 2,
                  padding: 2,
                  cursor: "pointer",
                }}
              >
                <FileUploadIcon sx={{ fontSize: 24 }} />
                <Box
                  sx={{
                    paddingTop: 1,
                    textAlign: "center",
                  }}
                >
                  <SbuxTranslate>{`Drag and drop file here, or click to select file`}</SbuxTranslate>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {files.length > 0 && (
          <Box>
            {files.map((file, idx) => (
              <Box
                key={idx}
                sx={{ display: "flex", alignItems: "center", marginTop: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {file.name}
                </Box>
                <Box sx={{ paddingLeft: 1 }}>
                  <IconButton
                    disableFocusRipple
                    disableRipple
                    onClick={removeFile}
                    sx={{
                      cursor: "pointer",
                      color: "black",
                      fontSize: 16,
                      padding: 0,
                      "&:focus": {
                        outline: 0,
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                marginTop: 2,
              }}
            >
              {files.length > 0 && (
                <audio
                  style={{
                    height: 26,
                    width: "100%",
                    maxWidth: 310,
                  }}
                  title={row.fileName}
                  onError={handleOnError}
                  controls
                  autoPlay
                >
                  <source src={base64String} type="audio/wav" />
                </audio>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </section>
  );
};

export default MediaMgtFileUpload;
